<template>
  <div class="badge-mapping-tab">
    <div class="badge-mapping-container">
      <div class="section-header">
        <h3>Badge Mapping</h3>
        <p class="section-description">
          Map ServiceM8 badges to {{ brand.agencyName }} tags. When a badge is applied to a job in ServiceM8, the corresponding tag will be added to the contact in {{ brand.agencyName }}.
        </p>
      </div>
      
      <div v-if="loading" class="loading-state">
        <p>Loading badges and tags...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadData" class="btn-retry">Retry</button>
      </div>
      
      <div v-else class="mapping-table-container">
        <div class="table-header">
          <div class="table-header-left">
            <h4>Badge Mappings</h4>
            <span class="badge-count">{{ filteredBadges.length }} badge(s)</span>
          </div>
          <div class="table-header-right">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search badges..." 
              class="search-input"
            />
            <button @click="loadData(true)" class="btn-refresh" :disabled="loading">
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
        
        <div v-if="filteredBadges.length === 0" class="no-data">
          <p>No badges found.</p>
        </div>
        
        <table v-else class="mapping-table">
          <thead>
            <tr>
              <th>ServiceM8 Badge</th>
              <th>Sync Direction</th>
              <th>{{ brand.agencyName }} Tag</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="badge in filteredBadges" :key="badge.uuid">
              <td class="badge-cell">
                <div class="badge-info">
                  <div class="badge-image-name">
                    <div 
                      v-if="badge.file_name" 
                      class="badge-image-container"
                      :style="{ backgroundImage: `url(${getBadgeImageUrl(badge.file_name)})` }"
                    ></div>
                    <div class="badge-name-wrapper">
                      <span class="badge-name">{{ badge.name }}</span>
                      <span v-if="badge.description" class="badge-description">{{ badge.description }}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="sync-direction-cell">
                <span class="sync-arrow" title="Sync flows both ways">↔</span>
              </td>
              <td class="tag-cell">
                <div class="tag-select-wrapper">
                  <select 
                    v-model="badgeMappings[badge.uuid]"
                    @change="handleTagChange(badge.uuid, $event)"
                    class="tag-select"
                  >
                    <option value="">Ignore</option>
                    <option 
                      v-for="tag in tags" 
                      :key="tag.id" 
                      :value="tag.id"
                    >
                      {{ tag.name }}
                    </option>
                  </select>
                  <button 
                    @click="openCreateTagModal(badge.uuid)"
                    class="btn-create-tag-plus"
                    :title="'Create a new tag'"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3V13M3 8H13" stroke="white" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Create Tag Modal -->
    <div v-if="showCreateTagModal" class="modal-overlay" @click.self="closeCreateTagModal">
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-title-group">
            <h3>Create New Tag</h3>
            <p>Enter a name for the new tag.</p>
          </div>
          <button @click="closeCreateTagModal" class="modal-close">×</button>
        </div>
        
        <div class="modal-body">
          <div class="modal-field">
            <label>Tag Name:</label>
            <input 
              v-model="newTagName"
              type="text" 
              placeholder="Enter tag name..."
              class="modal-input"
              @keyup.enter="createTagFromModal"
              @keyup.esc="closeCreateTagModal"
              ref="tagNameInput"
            />
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCreateTagModal" class="btn-modal-cancel">Cancel</button>
          <button @click="createTagFromModal" class="btn-modal-save" :disabled="!newTagName || !newTagName.trim()">
            Create Tag
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { brandMixin } from '../brandMixin'

export default {
  name: 'BadgeMappingTab',
  mixins: [brandMixin],
  data() {
    return {
      badges: [],
      tags: [],
      badgeMappings: {},
      loading: false,
      error: null,
      searchQuery: '',
      showCreateTagModal: false,
      creatingTagForBadgeUuid: null,
      newTagName: '',
      cacheKey: 'servicem8_badges_cache',
      cacheExpiry: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
    }
  },
  computed: {
    filteredBadges() {
      if (!this.searchQuery) {
        return this.badges;
      }
      const query = this.searchQuery.toLowerCase();
      return this.badges.filter(badge => 
        badge.name.toLowerCase().includes(query) ||
        (badge.description && badge.description.toLowerCase().includes(query))
      );
    },
  },
  async mounted() {
    await this.loadData(false); // Load with cache by default
  },
  methods: {
    async loadData(forceRefresh = false) {
      this.loading = true;
      this.error = null;
      
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        // Try to load badges from cache first (unless forcing refresh)
        let badgeData = null;
        if (!forceRefresh) {
          badgeData = this.getCachedBadges();
        }
        
        // Fetch badges if not cached or forcing refresh
        if (!badgeData || forceRefresh) {
          const badgeRes = await fetch('/servicem8/badges', { headers });
          if (badgeRes.ok) {
            badgeData = await badgeRes.json();
            // Cache the badges
            this.setCachedBadges(badgeData);
            console.log('Loaded badges from API and cached');
          } else {
            throw new Error('Failed to load ServiceM8 badges');
          }
        } else {
          console.log('Loaded badges from cache');
        }
        
        // Filter out inactive badges (active=0)
        this.badges = Array.isArray(badgeData) 
          ? badgeData.filter(b => b.active !== 0 && b.active !== '0')
          : [];
        console.log(`Displaying ${this.badges.length} active badge(s)`);
        
        // Always fetch tags fresh (they change more often)
        await this.loadTags();
        
        // Load badge mappings from database
        await this.loadBadgeMappings();
        
        // Initialize badge mappings for any badges without mappings
        this.badges.forEach(badge => {
          if (!this.badgeMappings[badge.uuid]) {
            this.badgeMappings[badge.uuid] = '';
          }
        });
      } catch (error) {
        console.error('Error loading badge mapping data:', error);
        this.error = error.message || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    getCachedBadges() {
      try {
        const cached = localStorage.getItem(this.cacheKey);
        if (!cached) return null;
        
        const data = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (data.timestamp && (now - data.timestamp) < this.cacheExpiry) {
          return data.badges;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(this.cacheKey);
          return null;
        }
      } catch (error) {
        console.error('Error reading badge cache:', error);
        return null;
      }
    },
    setCachedBadges(badges) {
      try {
        const cacheData = {
          badges: badges,
          timestamp: Date.now(),
        };
        localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.error('Error caching badges:', error);
      }
    },
    async handleTagChange(badgeUuid, event) {
      const selectedValue = event.target.value;
      
      try {
        if (selectedValue) {
          // Save mapping to database only when a tag is selected
          this.badgeMappings[badgeUuid] = selectedValue;
          await this.saveBadgeMapping(badgeUuid, selectedValue);
          console.log(`Successfully mapped badge ${badgeUuid} to tag ${selectedValue}`);
        } else {
          // Remove mapping from database when changed to "Ignore"
          this.badgeMappings[badgeUuid] = '';
          await this.deleteBadgeMapping(badgeUuid);
          console.log(`Successfully removed mapping for badge ${badgeUuid}`);
        }
      } catch (error) {
        // Revert the change if save failed
        console.error('Failed to update badge mapping, reverting change:', error);
        // Reload mappings to get the correct state
        await this.loadBadgeMappings();
      }
    },
    openCreateTagModal(badgeUuid) {
      this.creatingTagForBadgeUuid = badgeUuid;
      this.newTagName = '';
      this.showCreateTagModal = true;
      // Focus the input after modal is rendered
      this.$nextTick(() => {
        if (this.$refs.tagNameInput) {
          this.$refs.tagNameInput.focus();
        }
      });
    },
    closeCreateTagModal() {
      this.showCreateTagModal = false;
      this.creatingTagForBadgeUuid = null;
      this.newTagName = '';
    },
    async createTagFromModal() {
      const tagName = this.newTagName;
      if (!tagName || !tagName.trim()) {
        alert('Please enter a tag name');
        return;
      }
      
      const badgeUuid = this.creatingTagForBadgeUuid;
      if (!badgeUuid) {
        return;
      }
      
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 
          'x-sso-token': ssoToken,
          'Content-Type': 'application/json',
        };
        
        // Get locationId from user data
        const userData = await window.ghl.getUserData();
        const locationId = userData.locationId || userData.activeLocation;
        
        if (!locationId) {
          throw new Error('Location ID not found');
        }
        
        // Create tag via GHL API
        const response = await fetch(`/ghl/tags`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            locationId,
            name: tagName.trim(),
          }),
        });
        
        if (response.ok) {
          const tagData = await response.json();
          // GHL API returns { tag: { id, name, locationId } }
          const newTag = tagData.tag || tagData;
          
          // Add to tags list (avoid duplicates)
          const existingTag = this.tags.find(t => t.id === newTag.id);
          if (!existingTag) {
            this.tags.push(newTag);
          }
          
          // Set as selected for this badge
          this.badgeMappings[badgeUuid] = newTag.id;
          
          // Save mapping to database first
          await this.saveBadgeMapping(badgeUuid, newTag.id);
          
          // Close modal only after successful save
          this.closeCreateTagModal();
          
          console.log(`Tag "${tagName}" mapped to badge ${badgeUuid} (${existingTag ? 'existing tag' : 'new tag created'})`);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.message || errorData.error || 'Failed to create tag');
        }
      } catch (error) {
        console.error('Error creating tag:', error);
        alert(`Failed to create tag: ${error.message}`);
      }
    },
    async loadTags() {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const tagRes = await fetch('/ghl/tags', { headers });
        if (tagRes.ok) {
          const tagData = await tagRes.json();
          this.tags = tagData.tags || [];
          console.log(`Loaded ${this.tags.length} tag(s)`);
        } else {
          console.warn('Failed to load GHL tags, continuing without tags');
          this.tags = [];
        }
      } catch (error) {
        console.error('Error loading tags:', error);
        this.tags = [];
      }
    },
    async loadBadgeMappings() {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch('/badge-mappings', { headers });
        if (response.ok) {
          const mappings = await response.json();
          this.badgeMappings = { ...this.badgeMappings, ...mappings };
          console.log('Loaded badge mappings from database');
        } else {
          console.warn('Failed to load badge mappings, continuing without mappings');
        }
      } catch (error) {
        console.error('Error loading badge mappings:', error);
      }
    },
    async saveBadgeMapping(badgeUuid, tagId) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 
          'x-sso-token': ssoToken,
          'Content-Type': 'application/json',
        };
        
        const response = await fetch('/badge-mappings', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            badgeUuid,
            tagId,
          }),
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log(`Saved badge mapping: ${badgeUuid} -> ${tagId}`, result);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Failed to save badge mapping:', response.status, errorData);
          throw new Error(errorData.error || 'Failed to save badge mapping');
        }
      } catch (error) {
        console.error('Error saving badge mapping:', error);
        // Show error to user
        alert(`Failed to save badge mapping: ${error.message || 'Unknown error'}`);
        throw error;
      }
    },
    async deleteBadgeMapping(badgeUuid) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch(`/badge-mappings/${badgeUuid}`, {
          method: 'DELETE',
          headers,
        });
        
        if (response.ok) {
          console.log(`Deleted badge mapping for: ${badgeUuid}`);
        } else {
          console.error('Failed to delete badge mapping');
        }
      } catch (error) {
        console.error('Error deleting badge mapping:', error);
      }
    },
    getBadgeImageUrl(fileName) {
      if (!fileName) return null;
      
      // Check if it's already a full URL (e.g., S3 URLs for custom badges)
      // Standard badges have just a filename like "badges_large_001.png"
      // Custom badges have full URLs like "https://servicem8-nvc.s3.us-west-1.amazonaws.com/..."
      if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
        return fileName;
      }
      
      // For standard badges, append filename to base URL
      return `https://static-ap-southeast-2.servicem8.com/versions/100270/images/${fileName}`;
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
  },
}
</script>

<style scoped>
.badge-mapping-tab {
  padding: 20px;
}

.badge-mapping-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-header {
  margin-bottom: 20px;
  border-bottom: 2px solid #24ACFE;
  padding-bottom: 10px;
}

.section-header h3 {
  margin: 0 0 10px 0;
  color: #2c3e50;
}

.section-description {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.loading-state,
.error-state {
  padding: 40px;
  text-align: center;
  color: #666;
}

.error-state {
  color: #f44336;
}

.btn-retry {
  margin-top: 10px;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-retry:hover {
  filter: brightness(0.9);
}

.mapping-table-container {
  margin-top: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.table-header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.table-header-left h4 {
  margin: 0;
  color: #2c3e50;
}

.badge-count {
  color: #666;
  font-size: 14px;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 200px;
}

.search-input:focus {
  outline: none;
  border-color: #24ACFE;
}

.btn-refresh {
  padding: 6px 12px;
  background: #f5f5f5;
  color: #2c3e50;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #999;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.mapping-table th {
  background-color: #24ACFE;
  color: white;
  padding: 12px 15px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
}

.mapping-table td {
  padding: 15px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.mapping-table tbody tr:hover {
  background-color: #f9f9f9;
}

.badge-cell {
  width: 40%;
}

.sync-direction-cell {
  width: 10%;
  text-align: center;
  padding: 16px 8px !important;
  vertical-align: middle;
}

.sync-arrow {
  font-size: 32px;
  color: #24ACFE;
  font-weight: 900;
  display: inline-block;
  line-height: 1;
  letter-spacing: 2px;
}

.badge-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.badge-image-name {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge-image-container {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 4px;
  display: block;
  /* Background image approach for sprite cropping */
  background-size: 48px auto; /* Scale width to 48px, maintain aspect ratio (height becomes 144px) */
  background-position: 0px -96px; /* Move up by 96px to show bottom 48px (144px - 48px = 96px) */
  background-repeat: no-repeat;
}

.badge-name-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.badge-name {
  font-weight: 500;
  color: #2c3e50;
  font-size: 14px;
}

.badge-description {
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.tag-cell {
  width: 50%;
}

.tag-select-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-select {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.tag-select:focus {
  outline: none;
  border-color: #24ACFE;
}

.btn-create-tag-plus {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #24ACFE;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.1s;
  position: relative;
}

.btn-create-tag-plus:hover {
  background: #1a8fd8;
  transform: scale(1.05);
}

.btn-create-tag-plus:active {
  transform: scale(0.95);
}

/* Create Tag Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.modal-title-group h3 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.modal-title-group p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-field {
  margin-bottom: 20px;
}

.modal-field:last-child {
  margin-bottom: 0;
}

.modal-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: white;
  color: #1e293b;
}

.modal-input:focus {
  border-color: #24acfe;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn-modal-cancel {
  padding: 10px 20px;
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-modal-cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-modal-save {
  padding: 10px 20px;
  background: linear-gradient(135deg, rgb(0, 157, 255) 0%, rgb(0, 227, 255) 100%);
  color: white;
  border: 1px solid #24acfe;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-modal-save:hover:not(:disabled) {
  filter: brightness(0.9);
}

.btn-modal-save:disabled {
  background: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
